import { useEffect, useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import { api, BASE } from "@/lib/api";
import { PageHeader, Panel } from "@/pages/admin/shared";

const asDate = (s) => new Date(s + (s.endsWith("Z") || s.includes("+") ? "" : "Z")).toLocaleString();

export default function Votes() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const load = () => api.get("/admin/votes").then((r) => setRows(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => `${r.voter_name} ${r.voter_email} ${r.voter_phone} ${r.committee} ${r.category}`.toLowerCase().includes(s)) : rows;
  }, [q, rows]);

  return (
    <div data-testid="admin-votes">
      <PageHeader title="Votes" sub={`${rows.length} counted votes (latest ballot per voter only) · voter details are private to admins`}>
        <a href={`${BASE}/api/admin/contacts.csv`} data-testid="contacts-export-csv-btn" className="inline-flex items-center gap-2 rounded-full border border-maroon px-5 py-2.5 font-semibold text-maroon">
          <Download className="h-4 w-4" /> Opted-in contacts
        </a>
        <a href={`${BASE}/api/admin/export.csv`} data-testid="votes-export-csv-btn" className="inline-flex items-center gap-2 rounded-full bg-maroon px-5 py-2.5 font-semibold text-ivory">
          <Download className="h-4 w-4" /> Export CSV
        </a>
      </PageHeader>
      <label className="relative mb-3 block max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, committee…" data-testid="votes-search" className="h-10 w-full rounded-full border border-gold/40 bg-white pl-9 pr-3 text-sm outline-none focus:border-gold" />
      </label>
      <Panel className="overflow-x-auto p-0">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-ivory-dark text-xs uppercase tracking-wider text-ink-muted">
            <tr>{["Time", "Name", "Email", "Phone", "Updates", "Submissions", "Category", "Committee", "Voter hash"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`vote-row-${r.id}`} className="border-t border-gold/20">
                <td className="whitespace-nowrap px-4 py-3 tabular-nums">{asDate(r.created_at)}</td>
                <td className="px-4 py-3 font-medium">{r.voter_name || "—"}</td>
                <td className="px-4 py-3">{r.voter_email || "—"}</td>
                <td className="whitespace-nowrap px-4 py-3">{r.voter_phone || "—"}</td>
                <td className="px-4 py-3" data-testid={`vote-consent-${r.id}`}>{r.marketing_consent ? "Yes" : "No"}</td>
                <td className="px-4 py-3 tabular-nums" data-testid={`vote-submissions-${r.id}`}>{r.submissions_used}/3</td>
                <td className="px-4 py-3">{r.category}</td>
                <td className="px-4 py-3">{r.committee}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.voter_hash.slice(0, 10)}…</td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-muted" data-testid="votes-empty">No votes yet.</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
